import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiClient, useOptionalResourceChanges } from "@authe/ui-core/api";
import { useSession } from "@authe/ui-core/session";
import { connectResourceChanges, streamChannelIdsForCaller, subscribeDescriptor, useOptionalStreamingClient } from "@authe/streaming";
import { connectResourceCaches, resourceCacheBindings } from "@/api/resourceSync";
import { useTeamIds } from "./useTeamRoster";

/** One cache/stream bridge per authenticated dashboard, independent of routes. */
export function DashboardResourceSync(): null {
  const hub = useOptionalResourceChanges();
  const api = useApiClient();
  const queries = useQueryClient();
  const client = useOptionalStreamingClient();
  const { session, manager } = useSession();
  const teamIds = useTeamIds();
  const teamsKey = JSON.stringify([...teamIds].sort());
  const customerId = session?.tenant.customerId;
  const orgId = session?.tenant.orgId;
  const tenantId = session?.tenant.tenantId;
  const userId = session?.user.id;

  React.useEffect(() => {
    if (!hub) return;
    const disconnect = connectResourceCaches(hub, api, queries);
    // Clear caches synchronously at identity changes, before the next render.
    const offIdentity = manager.subscribeIdentityChange(disconnect);
    return () => { offIdentity(); disconnect(); };
  }, [hub, api, queries, manager]);

  React.useEffect(() => {
    if (!hub || !client) return;
    return connectResourceChanges(client, hub, resourceCacheBindings.flatMap(({ resource, stream }) => stream ? [{
      resource, stream,
      channels: streamChannelIdsForCaller({ customerId, orgId, tenantId, userId }, stream, JSON.parse(teamsKey) as string[]).map(subscribeDescriptor),
    }] : []));
  }, [hub, client, customerId, orgId, tenantId, userId, teamsKey]);
  return null;
}
